import { useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import PaletteIcon from "@mui/icons-material/Palette";
import SellIcon from "@mui/icons-material/Sell";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useWorkspace } from "../store/WorkspaceContext";
import {
    DEFAULT_APPEARANCE,
    DEFAULT_FOLDER_TAG_COLOR,
    DEFAULT_SAVE_MODE,
    SAVE_DEBOUNCE_MS,
    TAG_COLORS,
} from "../utils/constants";
import { getReadableTextColor } from "../utils/colors";
import AppearanceControls from "./AppearanceControls";
import ConfirmDialog from "./ConfirmDialog";
import {
    Label,
    Storage,
    Tune,
    WarningAmber,
} from "@mui/icons-material";

function SettingsTabPanel({ active, children }) {
    if (!active) {
        return null;
    }

    return <Box sx={{ pt: 2.5 }}>{children}</Box>;
}

export default function WorkspaceSettingsDialog({ open, onClose }) {
    const {
        settings,
        updateSettings,
        updateDefaultFolderTagColor,
        updateDefaultFileAppearance,
        exportWorkspace,
        importWorkspace,
        resetWorkspace,
    } = useWorkspace();
    const [tab, setTab] = useState("tags");
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [importConfirmOpen, setImportConfirmOpen] = useState(false);
    const [pendingImport, setPendingImport] = useState(null);
    const [dataStatus, setDataStatus] = useState("");
    const [dataError, setDataError] = useState("");
    const fileInputRef = useRef(null);
    const defaultTagColor =
        settings.defaultFolderTagColor || DEFAULT_FOLDER_TAG_COLOR;
    const defaultAppearance = {
        ...DEFAULT_APPEARANCE,
        ...(settings.defaultFileAppearance || {}),
    };
    const saveMode = settings.saveMode || DEFAULT_SAVE_MODE;

    const updateDefaultAppearance = (appearancePatch) => {
        const normalizedPatch = appearancePatch.backgroundColor
            ? {
                  ...appearancePatch,
                  textColor: getReadableTextColor(
                      appearancePatch.backgroundColor,
                  ),
              }
            : appearancePatch;

        updateDefaultFileAppearance(normalizedPatch);
    };

    const updateDefaultTag = (color) => {
        updateDefaultFolderTagColor(color);
    };

    const handleExport = () => {
        const blob = new Blob([JSON.stringify(exportWorkspace(), null, 2)], {
            type: "application/json;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");

        anchor.href = url;
        anchor.download = "textlab-workspace.json";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        setDataError("");
        setDataStatus("Workspace exported.");
    };

    const handleImportFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";

        if (!file) {
            return;
        }

        try {
            const nextWorkspace = JSON.parse(await file.text());
            setPendingImport(nextWorkspace);
            setImportConfirmOpen(true);
            setDataError("");
            setDataStatus("");
        } catch (error) {
            setPendingImport(null);
            setDataStatus("");
            setDataError(error.message || "Invalid backup file.");
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="md"
                slotProps={{
                    paper: {
                        sx: {
                            height: "75vh",
                            display: "flex",
                            flexDirection: "column",
                        },
                    },
                }}>
                <DialogTitle>Workspace Settings</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        flex: 1,
                        overflowY: "auto",
                    }}>
                    <Tabs
                        value={tab}
                        onChange={(_, nextTab) => setTab(nextTab)}
                        variant="scrollable"
                        scrollButtons="auto">
                        <Tab
                            icon={<SellIcon />}
                            iconPosition="start"
                            value="tags"
                            label="Tags"
                        />
                        <Tab
                            icon={<PaletteIcon />}
                            iconPosition="start"
                            value="appearance"
                            label="Appearance"
                        />
                        <Tab
                            icon={<DeleteForeverIcon />}
                            iconPosition="start"
                            value="data"
                            label="Data"
                        />
                    </Tabs>

                    <SettingsTabPanel active={tab === "tags"}>
                        <Stack spacing={2.5}>
                            {/* ================= Tags ================= */}
                            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        spacing={1.2}
                                        alignItems="center"
                                        sx={{ display: "flex" }}>
                                        <Label
                                            color="primary"
                                            sx={{
                                                fontSize: 22,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        />

                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                lineHeight: 1,
                                            }}>
                                            Default Folder Tag
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1.5, lineHeight: 1.7 }}>
                                        This color is applied automatically to
                                        new folders and cleared tags. It is
                                        excluded from filtering and tag
                                        selection lists.
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        sx={{
                                            flexWrap: "wrap",
                                            gap: 1,
                                            mt: 2,
                                        }}>
                                        {TAG_COLORS.map((tag) => {
                                            const selected =
                                                defaultTagColor === tag.value;

                                            return (
                                                <Button
                                                    key={tag.value}
                                                    onClick={() =>
                                                        updateDefaultTag(
                                                            tag.value,
                                                        )
                                                    }
                                                    variant={
                                                        selected
                                                            ? "contained"
                                                            : "outlined"
                                                    }
                                                    sx={{
                                                        minWidth: 120,
                                                        justifyContent:
                                                            "flex-start",
                                                        gap: 1,
                                                        borderRadius: 2,
                                                        textTransform: "none",
                                                        px: 1.5,
                                                    }}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: "50%",
                                                            bgcolor: tag.value,
                                                            flexShrink: 0,
                                                        }}
                                                    />

                                                    {tag.label}

                                                    {selected && (
                                                        <CheckIcon
                                                            fontSize="small"
                                                            sx={{ ml: "auto" }}
                                                        />
                                                    )}
                                                </Button>
                                            );
                                        })}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </SettingsTabPanel>

                    <SettingsTabPanel active={tab === "appearance"}>
                        <Stack spacing={2.5}>
                            {/* ================= Appearance ================= */}
                            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        spacing={1.2}
                                        alignItems="center"
                                        sx={{ display: "flex" }}>
                                        <Tune
                                            color="primary"
                                            sx={{
                                                fontSize: 22,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        />

                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                lineHeight: 1,
                                            }}>
                                            Appearance
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1 }}>
                                        Default file appearance settings applied
                                        to new files.
                                    </Typography>

                                    <Box sx={{ mt: 2 }}>
                                        <AppearanceControls
                                            appearance={defaultAppearance}
                                            onChange={updateDefaultAppearance}
                                            onReset={() =>
                                                updateDefaultFileAppearance(
                                                    { ...DEFAULT_APPEARANCE },
                                                    { replace: true },
                                                )
                                            }
                                            resetLabel="Reset appearance defaults"
                                        />
                                    </Box>
                                </CardContent>
                            </Card>
                        </Stack>
                    </SettingsTabPanel>

                    <SettingsTabPanel active={tab === "data"}>
                        <Stack spacing={2.5}>
                            {/* ================= Data ================= */}
                            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        spacing={1.2}
                                        alignItems="center"
                                        sx={{ display: "flex" }}>
                                        <Storage
                                            color="primary"
                                            sx={{
                                                fontSize: 22,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        />

                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                lineHeight: 1,
                                            }}>
                                            Local Data
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1 }}>
                                        Your files, folders, settings, and
                                        preferences are stored locally in this
                                        browser.
                                    </Typography>

                                    <Divider sx={{ my: 2 }} />

                                    <Stack spacing={1}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={800}>
                                            Save Timing
                                        </Typography>
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={saveMode}
                                            onChange={(_, nextMode) => {
                                                if (nextMode) {
                                                    updateSettings({
                                                        saveMode: nextMode,
                                                    });
                                                }
                                            }}
                                            sx={{
                                                alignSelf: "flex-start",
                                                "& .MuiToggleButton-root": {
                                                    textTransform: "none",
                                                    px: 2,
                                                    fontWeight: 800,
                                                },
                                            }}>
                                            <ToggleButton value="debounced">
                                                Debounced {SAVE_DEBOUNCE_MS}ms
                                            </ToggleButton>
                                            <ToggleButton value="immediate">
                                                Immediate
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </Stack>

                                    <Divider sx={{ my: 2 }} />

                                    <Typography
                                        variant="body2"
                                        fontWeight={800}
                                        sx={{ mb: 1.5 }}>
                                        Backup & Restore
                                    </Typography>

                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1.5}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DownloadIcon />}
                                            onClick={handleExport}>
                                            Export JSON
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            startIcon={<UploadFileIcon />}
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }>
                                            Import JSON
                                        </Button>
                                    </Stack>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="application/json"
                                        hidden
                                        onChange={handleImportFile}
                                    />

                                    {/* Status messages */}
                                    <Box sx={{ mt: 2 }}>
                                        {dataError && (
                                            <Typography
                                                variant="body2"
                                                color="error.main">
                                                {dataError}
                                            </Typography>
                                        )}

                                        {dataStatus && (
                                            <Typography
                                                variant="body2"
                                                color="success.main">
                                                {dataStatus}
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* ================= Danger Zone ================= */}
                            <Card
                                variant="outlined"
                                sx={{
                                    borderRadius: 3,
                                    borderColor: "error.light",
                                    backgroundColor: "error.50",
                                }}>
                                <CardContent>
                                    <Stack
                                        direction="row"
                                        spacing={1.2}
                                        alignItems="center"
                                        sx={{ display: "flex" }}>
                                        <WarningAmber
                                            color="error"
                                            sx={{
                                                fontSize: 22,
                                                display: "flex",
                                                alignItems: "center",
                                            }}
                                        />

                                        <Typography
                                            variant="h6"
                                            fontWeight={800}
                                            color="error"
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                lineHeight: 1,
                                            }}>
                                            Danger Zone
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 1 }}>
                                        This will permanently remove all local
                                        data including files, folders, and
                                        settings.
                                    </Typography>

                                    <Box sx={{ mt: 2 }}>
                                        <Button
                                            color="error"
                                            variant="contained"
                                            startIcon={<DeleteForeverIcon />}
                                            onClick={() =>
                                                setClearConfirmOpen(true)
                                            }>
                                            Clear all local data
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Stack>
                    </SettingsTabPanel>

                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} variant="contained">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={clearConfirmOpen}
                title="Clear Local Data"
                description="Delete all TextLab folders, files, favorites, tags, appearance settings, and UI settings from this browser? This cannot be undone."
                confirmLabel="Clear data"
                onConfirm={() => {
                    resetWorkspace();
                    setClearConfirmOpen(false);
                    onClose();
                }}
                onClose={() => setClearConfirmOpen(false)}
            />

            <ConfirmDialog
                open={importConfirmOpen}
                title="Import Workspace"
                description="Replace the current local TextLab workspace with the selected backup file?"
                confirmLabel="Import"
                onConfirm={() => {
                    try {
                        importWorkspace(pendingImport);
                        setDataError("");
                        setDataStatus("Workspace imported.");
                    } catch (error) {
                        setDataStatus("");
                        setDataError(
                            error.message || "Unable to import this backup.",
                        );
                    }
                    setPendingImport(null);
                    setImportConfirmOpen(false);
                }}
                onClose={() => {
                    setPendingImport(null);
                    setImportConfirmOpen(false);
                }}
            />
        </>
    );
}
